import React, { useEffect, useRef, useState } from 'react';
import ConfirmDialog from './ConfirmDialog';
import CancelButton from './CancelButton';

// Optim's own confirm / message / typed-value dialogs, in place of the
// browser's confirm(), alert() and prompt() boxes (issue #438). Callable
// from anywhere, like the browser's, but asynchronous:
//
//   if (!(await askConfirm('Delete this Activity?', { confirmLabel: 'Delete', destructive: true }))) return;
//   await showMessage('Could not save this tab');
//   const reason = await askText('Reason for the hold (optional):', { title: 'Put on Hold', confirmLabel: 'Put on Hold' });
//
// askConfirm resolves true/false, showMessage once it's closed, askText the
// typed value or null. Closing with ✕ or Escape is the same as Cancel.
// <AppDialogHost /> is mounted once at the root (index.js), so every screen,
// back office and Customer portal alike, gets the same dialog. Requests
// made while one is open wait their turn.

let enqueue = null;
let nextId = 1;

const request = (dialog) => new Promise((resolve) => {
  if (!enqueue) throw new Error('AppDialogHost is not mounted');
  enqueue({ ...dialog, id: nextId++, resolve });
});

export const askConfirm = (message, { title, confirmLabel = 'OK', destructive = false } = {}) =>
  request({ kind: 'confirm', message, title: title || `${confirmLabel}?`, confirmLabel, destructive });

export const showMessage = (message, { title = 'Message' } = {}) =>
  request({ kind: 'message', message, title, confirmLabel: 'OK' });

export const askText = (message, { title = 'Enter a Value', confirmLabel = 'OK', defaultValue = '' } = {}) =>
  request({ kind: 'text', message, title, confirmLabel, defaultValue });

export function AppDialogHost() {
  const [queue, setQueue] = useState([]);
  const [value, setValue] = useState('');
  const actionRef = useRef(null);
  const cancelRef = useRef(null);
  const inputRef = useRef(null);
  const current = queue[0];

  useEffect(() => {
    enqueue = (dialog) => setQueue((prev) => [...prev, dialog]);
    return () => { enqueue = null; };
  }, []);

  useEffect(() => {
    if (current?.kind === 'text') setValue(current.defaultValue);
  }, [current]);

  if (!current) return null;

  const cancelResult = { confirm: false, message: undefined, text: null }[current.kind];
  const finish = (result) => {
    current.resolve(result);
    setQueue((prev) => prev.slice(1));
  };
  const act = () => finish(current.kind === 'text' ? value : current.kind === 'confirm' ? true : undefined);

  // The user already chose the action, so Enter goes ahead (or, for a typed
  // value, the field has focus).
  const focusFirst = (e) => {
    e.preventDefault();
    const target = current.kind === 'text' ? inputRef : actionRef;
    target.current?.focus();
  };

  return (
    <ConfirmDialog
      key={current.id}
      open
      onOpenChange={(next) => { if (!next) finish(cancelResult); }}
      onOpenAutoFocus={focusFirst}
      title={current.title}
      footer={(
        <>
          {current.kind !== 'message' && <CancelButton ref={cancelRef} onClick={() => finish(cancelResult)} />}
          <button
            ref={actionRef}
            type="button"
            className={`confirm-dialog-action${current.destructive ? ' destructive' : ''}`}
            onClick={act}
          >
            {current.confirmLabel}
          </button>
        </>
      )}
    >
      <div className="confirm-dialog-message">{current.message}</div>
      {current.kind === 'text' && (
        <form onSubmit={(e) => { e.preventDefault(); act(); }}>
          <input
            ref={inputRef}
            className="styled-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            style={{ width: '100%', marginTop: 10, fontSize: 14 }}
          />
        </form>
      )}
    </ConfirmDialog>
  );
}
